import { makeVar } from '@apollo/client';

import type { LoggedUser, LoggedUserAccountData, RelyingParty } from '@/shared/lib/types/graphql';
import { migrateDataGridPreferences } from '@/shared/lib/util/dataGridPreferencesMigration';
import ls, { ACCESS_TOKEN_KEY } from '@/shared/lib/util/localstorage';
import { clearSessionValues } from '@/shared/lib/util/sessionValue';
import { useDataGridPreferencesStore } from '@/shared/model/DataGridPreferencesStore';
import { getPatientListNavigationState } from '@/shared/model/PatientListNavigationStore';
import { getVarPersisted } from './reactive-var';

export const currentLoggedUserVar = getVarPersisted(makeVar<LoggedUser | null>(null), 'th_currentLoggedUserVar');
export const currentLoggedRelyingParty = getVarPersisted(makeVar<RelyingParty | null>(null), 'th_currentRP');

// If a logged user was restored from persisted storage on app boot, hydrate
// the data grid preferences store immediately so grids can restore their saved
// state on first mount without waiting for a fresh login. Then kick off the
// one-shot localStorage → DB migration in the background.
//
// `client` is imported dynamically (not statically) to avoid a circular-import
// TDZ: `client.ts` imports `currentLoggedRelyingParty`/`currentLoggedUserVar` from
// this file and calls them synchronously at module-eval time. A static `import
// client from './client'` here would, whenever this module loads first, pull in
// `client.ts` before this module's own exports are initialized — leaving
// `currentLoggedRelyingParty` undefined when `client.ts` calls it.
{
  const bootUser = currentLoggedUserVar();
  if (bootUser?.preferences) {
    const preferences = bootUser.preferences;
    useDataGridPreferencesStore.getState().hydrateFromPreferences(preferences);
    // Only attempt the network migration if there's an actual access token — a persisted user object
    // with no token means an expired/stale session (e.g. redirected to /login), and the migration's
    // GraphQL mutation would just fail every time without ever setting its "done" flag, re-attempting
    // on every subsequent page load indefinitely.
    if (ls.get(ACCESS_TOKEN_KEY)) {
      setTimeout(() => {
        import('./client')
          .then(({ default: client }) => migrateDataGridPreferences(client))
          .catch(e => console.warn('Data grid prefs migration failed', e));
      }, 0);
    }
  }
}

export const loggedUserData = makeVar<LoggedUserAccountData | null>(null);

export const tempDueDate = makeVar(''); //TODO: BR -> This is a hack for the demo, we need to make sure we are updating value properly and updating as needed

// TODO
// this can be moved into the 'query' way of getting cached data
// https://www.apollographql.com/blog/apollo-client/caching/local-state-management-with-reactive-variables/
export const tempCalendarActiveStart = makeVar<Date>(new Date());

export const clearCache = async () => {
  currentLoggedUserVar(null);
  loggedUserData(null);
  currentLoggedRelyingParty(null);
  useDataGridPreferencesStore.getState().clear();
  clearSessionValues();
  getPatientListNavigationState().clearSession();
  const { default: client } = await import('./client');
  await client.cache.reset();
};
